import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Observable } from 'rxjs/Observable';
import screenfull from 'screenfull';
import { helpers } from '../helpers';

import {
  Utils
} from '../services';
import { Subject } from 'rxjs/Subject';

@Injectable()
export class PageEventsHandler {
  fullscreen$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  online$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  visibilityChange$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  fullscreenError$: Observable<any>;
  resize$: Observable<any>;
  focus$: Observable<boolean>;
  visible$: Observable<boolean>;
  mouseIn$: Observable<boolean>;
  mobilePause$: Subject<boolean> = new Subject();

  threshold: number = 160;

  documentOnkeyup;
  documentOnkeydown;

  constructor(
    private utils: Utils
  ) {
    let evtName, visibiltyProperty = this._getHiddenProp();

    let focus$ = Observable.merge(
        // Observable.fromEvent(document, 'click')
        //   .mapTo(true),
        Observable.fromEvent(window, 'focus')
          .mapTo(true));
    let blur$ = Observable.fromEvent(window, 'blur')
      .mapTo(false);

    this.documentOnkeyup = document.onkeyup;
    this.documentOnkeydown = document.onkeydown;

    this.visible$ = this.visibilityChange$
      .debounceTime(200)
      .distinctUntilChanged();

    this.focus$ = Observable.merge(
      blur$.delayWhen(() => Observable
        .timer(100)
        .takeUntil(focus$)),
      focus$);

    this.mouseIn$ = Observable.merge(
      Observable.fromEvent(document, 'mouseout')
        .mapTo(false),
      Observable.fromEvent(document, 'mouseover')
        .mapTo(true))
      .debounceTime(200);
      // .distinctUntilChanged();

    this.resize$ = Observable.fromEvent(window, 'resize')
      .mapTo(this._getViewportDetails());

    this.online$.next(navigator.onLine);
    window.addEventListener('online', () =>
      this.online$.next(true));
    window.addEventListener('offline', () =>
      this.online$.next(false));

    if (visibiltyProperty) {
      evtName = visibiltyProperty
        .replace(/[H|h]idden/, '') + 'visibilitychange';

      Observable.fromEvent(document, evtName)
        .subscribe(event =>
          this.visibilityChange$.next(this._isHidden()));
    }

    if (screenfull.enabled) {
      this.fullscreen$.next(screenfull.isFullscreen);

      document.addEventListener(screenfull.raw.fullscreenchange, () =>
        this.fullscreen$.next(screenfull.isFullscreen));

      this.fullscreenError$ = Observable.create($ =>
        document.addEventListener(screenfull.raw.fullscreenerror, error =>
          $.next(error)));

      this.fullscreenError$.subscribe(x => console.log('error ', x))
    } else {
      this._showAlert('screenfull disabled');
    }

    // mobile pause listener
    if(this.utils.isMobile()){
      document.addEventListener("pause", () => {
        this.mobilePause$.next(true);
      }, false);
    }
  }

  // NOTE: it doesn't care of browser zoom level
  _isDockConsoleOpen() {
    let widthThreshold = window.outerWidth - window.innerWidth > this.threshold
      , heightThreshold = window.outerHeight - window.innerHeight > this.threshold;

    return !(heightThreshold && widthThreshold) &&
      ((window['Firebug'] && window['Firebug'].chrome &&
        window['Firebug'].chrome.isInitialized)
        || widthThreshold || heightThreshold);
  }

  _isUnDockConsoleOpen() {
    let minResponseInMs = 100
      , before = new Date().getTime()
      , after;

    debugger;
    after = new Date().getTime();

    return after - before > minResponseInMs;
  }

  _areAlmostEqual(x, y, tolerance) {
    let min = Math.min(x, y)
      , max = Math.max(x, y);

    return max - min <= tolerance;
  }

  _showAlert(error) {
    console.error(error);
  }

  _getScreenDetails() {
    return {
      width: screen.width,
      height: screen.height,
      pixelRatio: window.devicePixelRatio
    };
  }

  _getViewportDetails() {
    let e: any = window
      , a = 'inner';

    if (!('innerWidth' in window)) {
      a = 'client';
      e = document.documentElement || document.body;
    }

    return {
      width: e[a + 'Width'],
      height: e[a + 'Height']
    };
  }

  _getHiddenProp() {
    let prefixes = ['webkit', 'moz', 'ms', 'o'];

    if ('hidden' in document) { return 'hidden'; }

    for (let i = 0; i < prefixes.length; i++) {
      if ((prefixes[i] + 'Hidden') in document)
        return prefixes[i] + 'Hidden';
    }

    return null;
  }

  _isHidden() {
    let prop = this._getHiddenProp();
    return prop ? document[prop] : false;
  }

  _exceedTolerance(prevSize, nextSize, pxTolerance) {
    return Math.abs(prevSize.height - nextSize.height) > pxTolerance ||
      Math.abs(prevSize.width - nextSize.width) > pxTolerance;
  }

  areDevToolsOpen() {
    return this._isUnDockConsoleOpen();
  }

  restoreBrowserShortcuts() {
    document.onkeydown = this.documentOnkeydown;
    document.onkeyup = this.documentOnkeyup;
  }

  disableConsole() {
    setInterval(() => console.clear(), 500);
  }

  disableBrowserShortcuts() {
    let toBlock = (keyCode) => {
      return (keyCode >= 112 && keyCode <= 123) ||
        keyCode === 19 ||
        keyCode === 35 ||
        keyCode === 44 ||
        keyCode === 42 ||
        keyCode === 36;
    };

    document.onkeydown = document.onkeyup = (e) => {
      if (e.key === '+' || e.key === '-') {
        return;
      }

      if (e && (e.ctrlKey || e.metaKey || toBlock(e.keyCode))) {
        if (e.keyCode === 44) {
          e.stopImmediatePropagation();
          helpers.copyToClipboard("Sorry ! Print screen is disabled!");
          setTimeout(function () {
            helpers.copyToClipboard("Sorry ! Print screen is disabled!");
          }, 350);
          setTimeout(function () {
            helpers.copyToClipboard("Sorry ! Print screen is disabled!");
          }, 700);
          setTimeout(function () {
            helpers.copyToClipboard("Sorry ! Print screen is disabled!");
          }, 1000);
          setTimeout(function () {
            helpers.copyToClipboard("Sorry ! Print screen is disabled!");
          }, 1500);
        }
        helpers.disabledEventPropagation(e);
        e.preventDefault();
        return false;
      }
    };
  }

  disableKeyboard() {
    let keyIsDown = {};

    document.onkeydown = overrideKeyboardEvent;
    document.onkeyup = overrideKeyboardEvent;

    function overrideKeyboardEvent(e) {
      if (e && e.type === 'keydown' &&
        !keyIsDown[e.keyCode]) {
        keyIsDown[e.keyCode] = true;
      } else if (e && e.type === 'keyup') {
        delete (keyIsDown[e.keyCode]);
      }

      helpers.disabledEventPropagation(e);
      e.preventDefault();
      return false;
    }
  }

  disableRightClick() {
    document.addEventListener('contextmenu', e => e.preventDefault());
  }

  exitFullscreen() {
    if (screenfull.enabled) {
      screenfull.exit();
    } else {
      this._showAlert('screenfull disabled');
    }
  }

  enableFullscreen() {
    if (screenfull.enabled) {
      screenfull.request();
    } else {
      this._showAlert('screenfull disabled');
    }
  }

  resizeInFullscreenMode$(pxTolerance?: number) {
    return this.fullscreen$
      .distinctUntilChanged()
      .filter(isFullscreen => isFullscreen)
      .delay(500)
      .switchMap(() => Observable.timer(1000)
        .mapTo(this._getViewportDetails())
        .switchMap(prev => this.resize$
          .mapTo(prev)
          .filter(prevSize =>
            this._exceedTolerance(
              prevSize,
              this._getViewportDetails(),
              pxTolerance || 1
            ))
          .debounceTime(1000)
          .takeUntil(this.fullscreen$.skip(1))
        ));
  }

  isUserOnPage$() {
    return Observable.merge(
      // TODO: fix it in fucking Opera (?!)
      // this.visible$,
      this.focus$
    );
  }

  isMobilePaused$() {
    return Observable.merge(
      this.mobilePause$
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/page-events-handler.service.ts